// Verträge für andere Module und fremde Systeme: Auswahlfelder (Artikel, Partner), Katalog für die öffentliche API
// und der Verlauf eines Stammdatensatzes.
import { PERMISSION_AUTHENTICATED, defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { zListPage, zListQuery } from '@shared/list'
import { z } from 'zod'
import { BARCODE_KINDS } from '../lib/gtin'
import { ITEM_KINDS, PACKAGINGS, PARTNER_ROLES } from '../lib/labels'
import { zActor, zDay, zTimestamp } from './common'

const zPriceMode = z.enum(['gross', 'net'])

// ---- Artikel für Auswahlfelder und Belege -------------------------------------------------------------------

const zLookupUnit = z.object({
  /** item_unit.id */
  id: zId,
  short: z.string(),
  label: z.string(),
  baseQty: z.int(),
  isBase: z.boolean(),
  decimals: z.int(),
  /** Standardgebinde der angefragten Richtung (Verkauf bzw. Einkauf) */
  isDefault: z.boolean(),
  /** Preisvorschlag je `pricePerQty` in der angefragten Basis; null = kein Preis hinterlegt (oder nicht sichtbar) */
  priceCents: z.int().nullable(),
  pricePerQty: z.int(),
  /** Pfand je Gebinde in der angefragten Basis */
  deposit: z
    .object({
      depositTypeId: zId,
      label: z.string(),
      unitPriceCents: z.int(),
      pricePerQty: z.int(),
      taxCodeId: zId.nullable(),
      taxRateBp: z.int().nullable(),
    })
    .nullable(),
})

export const zItemLookupRow = z.object({
  id: zId,
  sku: z.string(),
  name: z.string(),
  units: z.array(zLookupUnit),
  /** Bestand am angefragten Standort (Tausendstel der Basiseinheit); null = kein Standort genannt oder dort kein Leserecht */
  stockBaseQty: z.int().nullable(),
  taxCodeId: zId,
  taxRateBp: z.int().nullable(),
  lotRequired: z.boolean(),
  bestBeforeRequired: z.boolean(),
  inactive: z.boolean(),
})

export const itemLookup = defineContract({
  name: 'masterdata.item.lookup',
  title: 'Artikel für Belege nachschlagen',
  description: 'Artikel mit Gebinden, Preisvorschlag und Bestand am Standort – für Auswahlfelder und Belegzeilen.',
  llm: {
    description:
      'Liefert Artikel so, wie eine Belegzeile sie braucht: Gebinde mit Umrechnung, Preisvorschlag (Verkauf: aus der ' +
      'Preisfindung für Kunde, Standort und Datum; Einkauf: Preis des Lieferanten), Steuersatz und ' +
      'Bestand am Standort. Verwenden, bevor eine Belegzeile angelegt wird; für die reine Suche genügt masterdata.item.list.',
    keywords: ['Artikel wählen', 'Belegzeile', 'Preisvorschlag', 'Gebinde'],
  },
  input: z.strictObject({
    q: zListQuery,
    /** genau diese Artikel (geöffneter Beleg) – auch gesperrte */
    ids: z.array(zId).max(200).optional(),
    /** Standort des Belegs: Bestand dort, standortabhängige Preise */
    locationId: zId.nullish(),
    /** false: ohne Bestand – auch wenn ein Standort genannt ist (Blindzählung der Inventur). Standard: mit, wo erlaubt */
    stock: z.boolean().optional(),
    /** customer = Verkauf (Standard), supplier = Einkauf */
    role: z.enum(PARTNER_ROLES).optional(),
    partnerId: zId.nullish(),
    /** Basis, in der Preis und Pfand geliefert werden; Standard: die der Preisgruppe bzw. netto im Einkauf */
    priceMode: zPriceMode.optional(),
    date: zDay.optional(),
    page: zListPage,
  }),
  output: z.object({ rows: z.array(zItemLookupRow), nextCursor: z.string().nullable() }),
  permission: 'masterdata.item.read',
  risk: 'read',
  scope: 'any',
})

// ---- Partner für Auswahlfelder ------------------------------------------------------------------------------

export const zPartnerLookupRow = z.object({
  id: zId,
  number: z.string(),
  name: z.string(),
  name2: z.string().nullable(),
  isCustomer: z.boolean(),
  isSupplier: z.boolean(),
  addressLines: z.array(z.string()),
  city: z.string().nullable(),
  /** „14 Tage netto“ */
  paymentTerms: z.string().nullable(),
  priceGroup: z.string().nullable(),
  priceMode: zPriceMode.nullable(),
  /** Liefersperre, deaktiviert – ein Satz für die Auswahl */
  warning: z.string().nullable(),
})

export const partnerLookup = defineContract({
  name: 'masterdata.partner.lookup',
  title: 'Kunden und Lieferanten nachschlagen',
  description: 'Kunden oder Lieferanten mit Anschrift, Zahlungsbedingung und Preisgruppe – für Auswahlfelder in Belegen.',
  llm: {
    description:
      'Sucht Kunden (`role: customer`) oder Lieferanten (`role: supplier`) für einen Beleg und liefert Nummer, Name, ' +
      'Anschrift, Zahlungsbedingung, Preisgruppe und einen Warnhinweis bei Liefersperre. Ohne `role` beide, soweit ' +
      'der Nutzer sie lesen darf. Für alle Stammdaten masterdata.customer.get bzw. masterdata.supplier.get.',
    keywords: ['Kunde wählen', 'Lieferant wählen', 'Belegkopf', 'Partner'],
  },
  input: z.strictObject({
    q: zListQuery,
    ids: z.array(zId).max(200).optional(),
    role: z.enum(PARTNER_ROLES).optional(),
    page: zListPage,
  }),
  output: z.object({ rows: z.array(zPartnerLookupRow), nextCursor: z.string().nullable() }),
  permission: PERMISSION_AUTHENTICATED,
  risk: 'read',
})

// ---- Katalog für die öffentliche API (Bestell-App) ----------------------------------------------------------

const zCatalogUnit = z.object({
  id: zId,
  code: z.string(),
  name: z.string(),
  short: z.string(),
  label: z.string().nullable(),
  baseQty: z.int(),
  isBase: z.boolean(),
  isSalesDefault: z.boolean(),
  sellable: z.boolean(),
  depositTypeId: zId.nullable(),
  /** Pfand je Gebinde heute in Cent (Basis `depositBasis`); null = kein Pfand oder kein Betrag */
  depositCents: z.int().nullable(),
  depositBasis: zPriceMode.nullable(),
  grossWeightG: z.int().nullable(),
  barcodes: z.array(z.object({ code: z.string(), kind: z.enum(BARCODE_KINDS), isPrimary: z.boolean() })),
})

export const zCatalogItem = z.object({
  id: zId,
  sku: z.string(),
  name: z.string(),
  shortName: z.string().nullable(),
  description: z.string().nullable(),
  kind: z.enum(ITEM_KINDS),
  active: z.boolean(),
  group: z.object({ id: zId, name: z.string(), path: z.array(z.string()) }).nullable(),
  brand: z.string().nullable(),
  manufacturer: z.string().nullable(),
  countryOfOrigin: z.string().nullable(),
  taxCodeKey: z.string(),
  taxRateBp: z.int().nullable(),
  minAge: z.int(),
  alcoholBp: z.int().nullable(),
  packaging: z.enum(PACKAGINGS),
  contentQty: z.int().nullable(),
  contentUnit: z.string().nullable(),
  basePriceRequired: z.boolean(),
  imageUrls: z.array(z.string()),
  units: z.array(zCatalogUnit),
  updatedAt: zTimestamp,
})

export const catalogItems = defineContract({
  name: 'masterdata.catalog.items',
  title: 'Artikelkatalog abrufen',
  description: 'Artikel mit Gebinden und Barcodes zum Abgleich mit angebundenen Systemen (Bestell-App, Webshop).',
  llm: {
    description:
      'Liefert den Artikelkatalog seitenweise für Schnittstellen: Stammdaten, Gebinde mit Umrechnung und ' +
      'Barcodes. `updatedSince` liefert nur, was sich seitdem geändert hat (auch gesperrte Artikel – am Feld ' +
      '`active` erkennbar). Preise und Bestand haben eigene Aktionen. Im Gespräch besser masterdata.item.list.',
    keywords: ['Katalog', 'Schnittstelle', 'Bestell-App', 'Webshop', 'Abgleich', 'Export'],
  },
  input: z.strictObject({
    /** nur Artikel, die seit diesem Zeitpunkt (ISO 8601) geändert wurden */
    updatedSince: z.iso.datetime({ offset: true, error: 'Bitte einen Zeitpunkt nach ISO 8601 angeben (2026-10-12T08:00:00Z).' }).optional(),
    /** Standard: nur aktive; mit `updatedSince` immer alle */
    includeInactive: z.boolean().default(false),
    itemGroupId: zId.optional(),
    page: zListPage,
  }),
  output: z.object({ rows: z.array(zCatalogItem), nextCursor: z.string().nullable() }),
  permission: 'masterdata.item.read',
  risk: 'read',
  scope: 'any',
  publicApi: true,
})

export const catalogGroups = defineContract({
  name: 'masterdata.catalog.groups',
  title: 'Warengruppen des Katalogs abrufen',
  description: 'Aktive Warengruppen als Baum zum Abgleich mit angebundenen Systemen.',
  llm: {
    description: 'Liefert die aktiven Warengruppen mit Pfad für Schnittstellen. Im Gespräch besser masterdata.itemGroup.list.',
    keywords: ['Katalog', 'Warengruppen', 'Schnittstelle'],
  },
  input: z.strictObject({}),
  output: z.object({
    rows: z.array(z.object({ id: zId, parentId: zId.nullable(), code: z.string().nullable(), name: z.string(), path: z.array(z.string()), sort: z.int() })),
  }),
  permission: 'masterdata.item.read',
  risk: 'read',
  scope: 'any',
  publicApi: true,
})

// ---- Verlauf eines Stammdatensatzes -------------------------------------------------------------------------

export const HISTORY_ENTITIES = [
  'item',
  'customer',
  'supplier',
  'location',
  'bin',
  'unit',
  'tax_code',
  'item_group',
  'payment_term',
  'reason_code',
] as const
export type HistoryEntity = (typeof HISTORY_ENTITIES)[number]

export const zHistoryEntry = z.object({
  id: z.string(),
  at: zTimestamp,
  actor: zActor,
  /** ui, api, assistant, import, system */
  channel: z.string(),
  viaAssistant: z.boolean(),
  /** audit = Eintrag einer Aktion mit Klartext · version = Stand der Zeile vor einer Änderung ohne Aktion (Seed, Wartung) */
  source: z.enum(['audit', 'version']),
  summary: z.string(),
  reason: z.string().nullable(),
  changes: z.array(z.object({ field: z.string(), label: z.string(), before: z.string().nullable(), after: z.string().nullable() })),
})

export const historyList = defineContract({
  name: 'masterdata.history.list',
  title: 'Verlauf eines Stammdatensatzes anzeigen',
  description: 'Wer hat einen Artikel, Kunden, Lieferanten, Standort oder Grunddatensatz wann wie geändert.',
  llm: {
    description:
      'Liefert den Verlauf EINES Stammdatensatzes, jüngster Eintrag zuerst: Zeitpunkt, Person, Weg (Oberfläche, ' +
      'Import, Assistent), Klartext und die geänderten Felder mit Vorher/Nachher. Verwenden für „Wer hat den ' +
      'Meldebestand geändert?“ oder „Seit wann gilt dieser Einkaufspreis?“. Einkaufspreise erscheinen nur mit dem ' +
      'Recht „Einstandspreise sehen“.',
    keywords: ['Verlauf', 'Historie', 'wer hat geändert', 'Änderung', 'Protokoll'],
  },
  input: z.strictObject({ entity: z.enum(HISTORY_ENTITIES), id: zId, page: zListPage }),
  output: z.object({ rows: z.array(zHistoryEntry), nextCursor: z.string().nullable() }),
  permission: PERMISSION_AUTHENTICATED,
  risk: 'read',
})
